import { EARTH, type FlightOptions, type MissionSimulationOptions } from '@trajectory/astro';
import type { LaunchSiteDto, MissionDto, RocketDto } from '@trajectory/contracts';
import { buildFlightOptions, buildMissionSimulationOptions } from './ascent-program';
import { toPhysicsVehicle } from './vehicle-adapter';

export type SimulationPresetId = 'suborbital-demo' | 'leo-demo' | 'lunar-demo';

export interface SimulationPreset {
  id: SimulationPresetId;
  name: string;
  description: string;
}

/** Small, predictable starter flights that do not modify the open mission or rocket. */
export const SIMULATION_PRESETS: readonly SimulationPreset[] = [
  {
    id: 'suborbital-demo',
    name: 'Suborbital hop',
    description: 'A short 120 km flight for learning playback, maps, and telemetry.',
  },
  {
    id: 'leo-demo',
    name: '200 km LEO',
    description: 'A complete staged ascent toward a circular low Earth orbit.',
  },
  {
    id: 'lunar-demo',
    name: 'Lunar transfer',
    description:
      'Launch, trans-lunar injection, moving-Moon encounter, and lunar arrival playback.',
  },
] as const;

export function buildSimulationPreset(
  presetId: SimulationPresetId,
  rocket: RocketDto,
  launchSite: LaunchSiteDto,
  launchAt = new Date().toISOString(),
): FlightOptions | MissionSimulationOptions {
  const suborbital = presetId === 'suborbital-demo';
  const lunar = presetId === 'lunar-demo';
  const targetAltitude = suborbital ? 120_000 : 200_000;
  const mission: MissionDto = {
    id: presetId,
    name: SIMULATION_PRESETS.find((preset) => preset.id === presetId)!.name,
    description: 'Built-in quick-start simulation preset.',
    missionType: suborbital ? 'suborbital' : lunar ? 'lunar' : 'leo',
    launchAt,
    launchSiteId: launchSite.id,
    destinationBody: lunar ? 'moon' : 'earth',
    payloadMassKg: Math.min(rocket.payloadMassKg, suborbital ? 500 : lunar ? 1_000 : 5_000),
    crewKind: 'cargo',
    crewCount: 0,
    rocketId: null,
    targetOrbit: lunar
      ? undefined
      : {
          semiMajorAxisMeters: EARTH.equatorialRadius + targetAltitude,
          eccentricity: 0,
          inclinationDeg: Math.max(Math.abs(launchSite.latitudeDeg), 28.5),
          raanDeg: 0,
          argumentOfPeriapsisDeg: 0,
          trueAnomalyDeg: 0,
        },
    ascentProgram: {
      azimuthDeg: 90,
      pitchStartAltitudeMeters: 500,
      turnEndAltitudeMeters: suborbital ? 80_000 : 120_000,
      turnExponent: 0.5,
      targetApoapsisAltitudeMeters: targetAltitude,
      targetPeriapsisAltitudeMeters: suborbital ? -100_000 : targetAltitude,
      closedLoopAltitudeMeters: 55_000,
      verticalSpeedTimeConstantSeconds: 200,
      maxAngleOfAttackDeg: 6,
      angleOfAttackQThresholdPa: 2_000,
      maxQThrottleThresholdPa: 35_000,
      maxQThrottle: 0.75,
    },
    maneuvers: [],
    tags: ['demo'],
  };

  const input = {
    mission,
    vehicle: toPhysicsVehicle(rocket),
    launchSite,
    maxDurationSeconds: suborbital ? 900 : 1_800,
  };
  return suborbital ? buildFlightOptions(input) : buildMissionSimulationOptions(input);
}
